import {
  ChangeEvent,
  memo,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  useIsAllowedPermission,
  usePermissions,
} from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';

import { Box, Input } from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../../constants/campaignBuilder.js';
import { useCellValueUpdate } from '../../../../../hooks/useCellValueUpdate.js';
import { TRACKING_EVENTS } from '../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../types.js';
import FormattedField from './FormattedField/FormattedField.jsx';
import TableEditCell from './TableEditCell/TableEditCell.jsx';

type Props = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const TextInput = memo(({ value, column, row, cellColor }: Props) => {
  const { t } = useTranslation('@myn/planning');
  const {
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    type,
    scale,
    isPredictivePlanningMode,
    isArchivedVersion,
  } = row.original;
  const inputRef = useRef<HTMLInputElement>(null);

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value ?? '');

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const { permissions } = usePermissions();
  const isNonFinancialUser = !permissions.financialDataFeature;

  const { updateCellValue } = useCellValueUpdate({
    row,
    column,
  });

  const isEmpty = !isFocused && !inputValue;

  const isReadOnlyField =
    !isAllowed ||
    isPredictivePlanningMode ||
    isNonFinancialUser ||
    isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;

  const isTotal = type === BUILDER_ROW_TYPES.TOTAL;
  const isDisabled = type !== BUILDER_ROW_TYPES.TILE && column.onlyTileEditable;

  const isCloseBtnShown = !!inputValue && !isFocused && !isDisabled;

  const { startMeasurePerformance } = column;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
    setInputValue(e.target.value);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleValueUpdate = (newValue: string) => {
    setInputValue(newValue);
    setIsFocused(false);
  };

  const handleBlur = async () => {
    setIsFocused(false);
    if (!inputValue && !value) {
      handleValueUpdate('');
      return;
    }

    const currentValue = String(value);

    if (currentValue !== inputValue) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

      const newValue = await updateCellValue({
        currentValue,
        newValue: inputValue,
      });

      handleValueUpdate(String(newValue));
    }
  };

  const handleReset = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

    await updateCellValue({
      currentValue: String(value),
      newValue: null,
    });
    handleValueUpdate('');
  };

  useEffect(() => {
    if (value !== null && value !== inputValue) {
      setInputValue(value ?? '');
    }
  }, [value]);

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  if (isTotal || isDisabled) {
    return null;
  }

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        value={value}
        color={cellColor}
        variant="text"
      />
    );
  }

  if (!isFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={isCloseBtnShown}
        deleteValueFunc={handleReset}
        showEditModeFunc={handleFocus}
        startMeasurePerformance={startMeasurePerformance}
        value={inputValue}
        variant="text"
      />
    );
  }

  return (
    <InputWrapper>
      <StyledTextInput
        type="text"
        inputRef={inputRef}
        onChange={handleChange}
        value={inputValue}
        onBlur={handleBlur}
        placeholder={isEmpty ? t('Insert values') : ''}
        scale={scale}
      />
    </InputWrapper>
  );
});

const InputWrapper = styled(Box)`
  height: 100%;
  width: 100%;
  & > div {
    height: 100%;
  }
`;

const StyledTextInput = styled(Input)<{
  scale: number;
}>`
  box-shadow: none;
  min-height: 0;
  width: 100%;
  height: 100%;
  background: transparent;

  input {
    user-select: none;
    cursor: default;
  }

  ${({ scale }) => css`
    font-size: ${0.875 * scale}rem;
    line-height: ${1.25 * scale};
  `}
`;

export default TextInput;
